import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import setAttendeeName from '@shoflo/studio-redux/actions/attendee/setAttendeeName';
import clearKnock from '@shoflo/studio-redux/actions/knock/clearKnock';
import cancelDisconnectSource from '@shoflo/studio-redux/actions/source/cancelDisconnectSource';
import {
  BROADCAST_ACCEPT_KNOCK,
  BROADCAST_UPDATE_KNOCK
} from '@shoflo/studio-redux/actions/knock/types';
import { getBroadcastRouteMatch } from '@shoflo/studio-redux/utils/routeMatch';
import {
  getApiClient,
  isValidKnock,
  handleFailedCreateAttendee,
  sendMeetingToAttendee,
  getKnockPayload,
  createAttendee,
  getGenericKnockPayload
} from './utils';
import createSource from 'actions/source/createSource';
import updateSource from 'actions/source/updateSource';
import { createSourceFactory } from 'utils/actionUtils';
import { SOURCE_ROLE_SPEAKER } from '@shoflo/polymer/constants/SourceRoleTypes';
import { CAMERA_PRESENTER } from '@shoflo/polymer/constants/SourceItems';
import { UNASSIGNED } from '@studio/constants/Sources';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import { VENDOR_AGORA } from '@studio/constants/Vendor';

/**
 * Emits an event to the accepted Presenter socket
 *
 * @param {*} socket
 */
export function acceptedKnockMiddleware(socket) {
  return store => next => async action => {
    const targetdActions = [
      `${BROADCAST_ACCEPT_KNOCK}`,
      `${BROADCAST_UPDATE_KNOCK}_SUCCESS`
    ];
    from([action])
      .pipe(filter($action => targetdActions.includes($action.type)))
      .subscribe(async $action => {
        if (!isValidKnock($action)) return;
        const apiClient = getApiClient();

        const { dispatch, getState } = store;

        let {
          meeting,
          attendeeRoster,
          program,
          team,
          attendeeCameraStatus,
          broadcastSettings,
          banners,
          streamEndpoints
        } = getState();

        const match = getBroadcastRouteMatch(getState);

        const { teamId, eventId, broadcastId } = match.params;
        const useAgora =
          shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

        if (useAgora) {
          const payload = getGenericKnockPayload({
            getState,
            action: $action,
            banners,
            streamEndpoints,
            attendeeRoster,
            program,
            broadcastSettings,
            team
          });

          sendMeetingToAttendee(socket, payload);
          dispatch(clearKnock($action.payload));
          return;
        }
        // Check to see if the meeting is in session
        if (!meeting.meetingSession) return;

        // Find matching disconnected/unassigned source BEFORE creating attendee
        // so we can pass sourceId to the backend for proper association
        const { broadcastSources: { sources = [] } = {} } = getState();
        const knockName = $action.payload.name
          ?.toLowerCase()
          .replaceAll(/\s+/g, '');
        const matchingSource = sources.find(
          source =>
            source.type === CAMERA_PRESENTER &&
            source.title?.toLowerCase().replaceAll(/\s+/g, '') === knockName &&
            (source.disconnected || source.value === UNASSIGNED)
        );
        const matchedSourceId = matchingSource?._id;

        // Clear the knock from state
        dispatch(clearKnock($action.payload));

        let createdAttendee;

        // Create the attendee (pass sourceId for backend association if reusing)
        try {
          createdAttendee = await createAttendee(
            apiClient,
            $action,
            teamId,
            eventId,
            meeting,
            matchedSourceId
          );
        } catch (e) {
          handleFailedCreateAttendee(e, socket, $action, store);
        }

        if (!createdAttendee) return;

        const attendeeId = createdAttendee.attendeeResponse.Attendee.AttendeeId;

        // The 'presenterSessionId' is utilized when a presenter joins a Chime meeting SLI.
        // It follows the format 'presenterSessionId: Presenter-{presenterId}-{sessionId}'.
        const presenterSessionId =
          createdAttendee.attendeeResponse.Attendee.ExternalUserId;

        dispatch(
          setAttendeeName(
            attendeeId,
            $action.payload.name,
            '',
            SOURCE_ROLE_SPEAKER
          )
        );

        const knockPayload = getKnockPayload({
          getState,
          action: $action,
          createdAttendee,
          attendeeCameraStatus,
          banners,
          streamEndpoints,
          meeting,
          attendeeRoster,
          program,
          broadcastSettings,
          team
        });

        sendMeetingToAttendee(socket, knockPayload);

        if (matchingSource) {
          // Cancel pending disconnect timeout (prevents source deletion)
          dispatch(cancelDisconnectSource(matchingSource._id));

          // Reuse existing source — only send fields the API supports
          const updateFields = {
            value: attendeeId,
            title: $action.payload.name,
            description: $action.payload.description,
            cameraUnavailable: $action.payload.cameraUnavailable
          };

          if ($action.payload?.userId) {
            updateFields.userId = $action.payload.userId;
            updateFields.role = SOURCE_ROLE_SPEAKER;
          }

          store.dispatch(updateSource(matchingSource._id, updateFields));
        } else {
          const newSource = createSourceFactory({
            broadcastId,
            eventId,
            teamId,
            attendeeId,
            title: $action.payload.name,
            description: $action.payload.description,
            isInitializing: true,
            presenterPhoto: $action.payload?.presenterPhoto,
            userId: $action?.payload?.userId,
            role: SOURCE_ROLE_SPEAKER,
            presenterSessionId,
            cameraUnavailable: $action.payload.cameraUnavailable
          });

          store.dispatch(createSource(newSource, true));
        }
      });
    return next(action);
  };
}

export default acceptedKnockMiddleware;
